<script lang="ts" setup>
    import User from '~~/app/auth/models/User';
    import useUserAvatarDisplay from '~/composables/useUserAvatarDisplay';

    interface Props {
        user: User | null;
        size?: string;
        tooltipPosition?: string;
        clickableAvatar?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        size: 'w-6 h-6 lg:h-8 lg:w-8',
        tooltipPosition: 'top',
        clickableAvatar: true,
    });

    const display = computed(() => useUserAvatarDisplay(props.user));

    // The avatar URL may point at an image the API no longer serves, so presence alone
    // cannot be trusted: only a successful load rules out the initials fallback.
    const imageFailed = ref(false);

    // Keyed on the user too: two users can share an avatar URL, and watching the URL alone would
    // keep a previous load failure latched when the component is reused for a different user.
    watch([() => props.user?.getId(), () => display.value.imageUrl], () => {
        imageFailed.value = false;
    });

    const showImage = computed(() => Boolean(display.value.imageUrl) && !imageFailed.value);

    const onClick = (event: Event) => {
        if (!props.clickableAvatar) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        return props.user?.preview();
    };
</script>

<template>
    <div
        class="relative overflow-hidden rounded-full cursor-pointer"
        :class="[size, user?.isInactive ? 'grayscale opacity-75' : '']"
        :data-tooltip="user ? user.name : null"
        :data-tooltip-location="tooltipPosition"
        @click="onClick"
    >
        <img
            v-if="showImage"
            :src="display.imageUrl!"
            :alt="user?.name ?? ''"
            class="object-cover w-full h-full"
            @error="imageFailed = true"
        />
        <div
            v-else
            class="flex items-center justify-center w-full h-full"
            :style="{ backgroundColor: display.backgroundColor, color: display.textColor }"
        >
            <span
                v-if="display.initials"
                class="text-2xs font-bold leading-none select-none"
            >
                {{ display.initials }}
            </span>
        </div>
    </div>
</template>
